import { Box, CircularProgress, Typography } from '@mui/material';
import Fade from '@mui/material/Fade';
import { useEffect, useState } from 'react';

/**
 * The properties for the loading circle
 */
interface LoadingCircleProps {
  /**
   * True, to show the loading circle. False, otherwise.
   */
  isLoading: boolean;
  /**
   * (Optional) True, to hide the text. False, otherwise. Defaults to false.
   */
  hideText?: boolean;
  /**
   * (Optional) True to blur the edges of the overlay. False, otherwise. Defaults to false.
   */
  blurEdges?: boolean;
  /**
   * (Optional) True, to hide the background shadow overlay. Defaults to false.
   */
  hideBackgroundShadow?: boolean;
  /**
   * (Optional) The loading text to show. Defaults to "Loading..."
   */
  loadingText?: string;
  /**
   * (Optional) The amount of time to wait before showing the loading circle
   */
  loadingDelayMs?: number;
}

/**
 * This loading circle is a component that can be used whenever data is loading. It is designed to only
 * show after an initial delay that's equal to the human reaction time. This prevents a flickering of this
 * loading circle when loading occurs quickly.
 *
 * !IMPORTANT: Ensure this component is wrapped with a component that has a 'relative' position. This loading
 *   circle uses an absolute position to center the text and apply a blur darkened backdrop.
 */
const LoadingCircle = ({
  isLoading,
  blurEdges,
  hideBackgroundShadow,
  loadingText,
  loadingDelayMs = 200,
  hideText = false,
}: LoadingCircleProps) => {
  const [showLoading, setShowLoading] = useState(false);
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout>();

  useEffect(() => {
    if (isLoading) {
      // Only show the loading circle after a small amount of time, then recheck to ensure it should
      //  still be shown.
      // - There's a lot of research around how long we should wait to show the loading circle,
      //   the popular answer is about 1 second, but there are many different ideas. We're using
      //   the idea of reaction time. So, if the content can load in the typical reaction time of
      //   a human, then we assume they won't be able to notice the delay and we don't need to
      //   show the circle
      const timeout = setTimeout(() => {
        if (isLoading) {
          setShowLoading(true);
        }
      }, loadingDelayMs);
      setTimeoutId((t) => {
        clearTimeout(t);
        return timeout;
      });
    } else {
      setShowLoading(false);
    }
  }, [isLoading, loadingDelayMs]);

  useEffect(() => {
    if (isLoading) return;

    clearTimeout(timeoutId);
  }, [isLoading, timeoutId]);

  return (
    // We add a fade animation showing the loading circle to account for the use cases where we wait
    //  the initial amount of time and the loading circle shows. Imagine we wait 300ms then show it,
    //  but the actual load time was 350ms. Since we fade in, the loading should be barely  noticeable
    //  even when things don't time perfectly
    <Fade in={showLoading} timeout={100}>
      <Box
        position="absolute"
        sx={{
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
        }}
      >
        <Box
          display={showLoading ? 'flex' : 'none'}
          alignItems="center"
          justifyContent="center"
          sx={{
            backgroundColor: hideBackgroundShadow ? 'initial' : '#00000066',
            boxShadow: blurEdges ? '0 0 15px 10px #3c3c3c94' : 'unset',
            width: '100%',
            height: '100%',
            backdropFilter: 'blur(5px)',
          }}
        >
          <CircularProgress size="2.5em" thickness={5} />
          {!hideText && (
            <Typography fontSize="2.5em" sx={{ marginLeft: '.75em' }}>
              {loadingText}
            </Typography>
          )}
        </Box>
      </Box>
    </Fade>
  );
};

LoadingCircle.defaultProps = {
  blurEdges: false,
  loadingText: 'Loading...',
};

export default LoadingCircle;
